import { Box, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { type EPLITrainerDevelopmentPlan } from '../../../models/EPLITrainerDevelopmentPlan'
import { Modal } from '../../customComponents/Modal'
import { EPLITrainerDevelopmentPlanAttendeeCompletionStatus } from '../../../models/EPLITrainerDevelopmentPlanAttendeeCompletionStatus'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useEPLITrainerDevelopmentPlanModal } from '../../../hooks/use-epliTrainerDevelopmentPlans'
import { type UpdateEPLITrainerDevelopmentPlanFormValues, type AttendeeFormValues } from './EPLITrainerDevelopmentPlanModal'
import { UpdateEPLITrainerDevelopmentPlan } from '../../../services/AnnualPlanService'

export interface EPLITrainerDevelopmentPlanTrackingFormValues {
  attendees: AttendeeFormValues[]
  actualAmount: string | number | null
}
interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan
  implementationPhase: boolean
}
export const EPLITrainerDevelopmentPlanTrackingModal = (props: Props): JSX.Element => {
  const isUpdate: boolean = props.epliTrainerDevelopmentPlan.isUpdate ?? false
  const { attendeeToFormValues } = useEPLITrainerDevelopmentPlanModal()
  const [formValues, setFormValues] = useState<EPLITrainerDevelopmentPlanTrackingFormValues>({
    attendees: props.epliTrainerDevelopmentPlan.attendees?.map(attendeeToFormValues) ?? [],
    actualAmount: props.epliTrainerDevelopmentPlan.actualBudgetAmount ?? null
  })
  const [submitted, setSubmitted] = useState<boolean>(false)

  const isValidNumber = (value: string | number | null): boolean => {
    return value !== null && value !== '' && (!isNaN(Number(value)) && Number(value) >= 0)
  }
  const attendeeFormValidators = (index: number): Record<keyof AttendeeFormValues, { required: boolean, isValid: boolean }> => ({
    id: { required: true, isValid: true },
    fullName: { required: true, isValid: true },
    isdId: { required: true, isValid: true },
    isAttendeeFromDistrict: { required: true, isValid: formValues.attendees[index]?.isAttendeeFromDistrict != null },
    districtId: { required: true, isValid: true },
    status: { required: true, isValid: true },
    completionStatus: { required: true, isValid: formValues.attendees[index]?.completionStatus != null && formValues.attendees[index]?.completionStatus?.trim() !== '' && Object.values(EPLITrainerDevelopmentPlanAttendeeCompletionStatus)?.includes(formValues.attendees[index]?.completionStatus as EPLITrainerDevelopmentPlanAttendeeCompletionStatus) }
  })
  const hasInvalidAttendees = formValues.attendees.length !== props.epliTrainerDevelopmentPlan.numberOfAttendees || formValues.attendees.some(a => a.completionStatus == null || a.completionStatus.trim() === '' || !Object.values(EPLITrainerDevelopmentPlanAttendeeCompletionStatus).includes(a.completionStatus as EPLITrainerDevelopmentPlanAttendeeCompletionStatus))
  const formValidators: Record<keyof EPLITrainerDevelopmentPlanTrackingFormValues, { required: boolean, isValid: boolean }> = {
    attendees: { required: true, isValid: !hasInvalidAttendees },
    actualAmount: { required: false, isValid: formValues.actualAmount == null || formValues.actualAmount === '' || isValidNumber(formValues.actualAmount) }
  }
  const hasInvalidFormValues = Object.values(formValidators).some(v => !v.isValid)

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleAttendeeFieldUpdate = (index: number, e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    const updatedAttendees = [...formValues.attendees]
    updatedAttendees[index] = { ...updatedAttendees[index], [name]: value }
    setFormValues({ ...formValues, attendees: updatedAttendees })
  }

  const handleSubmit = async (): Promise<void> => {
    if (!isUpdate) {
      toast.error('This plan has no updates to track.')
      props.onClose()
      return
    }
    if (formValues.actualAmount != null && formValues.actualAmount !== '' && !isValidNumber(formValues.actualAmount)) {
      toast.error('Actual Amount must be greater than or equal to 0')
      return
    }
    setSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const cleanAttendees = formValues.attendees.map(a => ({
      id: a.id,
      fullName: a.fullName,
      isAttendeeFromDistrict: a.isAttendeeFromDistrict,
      isdId: a.isdId,
      districtId: a.districtId,
      status: a.status,
      completionStatus: a.completionStatus
    }))

    const updatedPlan: UpdateEPLITrainerDevelopmentPlanFormValues = {
      id: props.epliTrainerDevelopmentPlan.id,
      priorityId: props.epliTrainerDevelopmentPlan.priorityId,
      epliTrainingId: props.epliTrainerDevelopmentPlan.epliTraining.id ?? null,
      numberOfAttendees: props.epliTrainerDevelopmentPlan.numberOfAttendees,
      budgetRequest: props.epliTrainerDevelopmentPlan.budgetRequest,
      actualAmount: isValidNumber(formValues.actualAmount) ? Number(formValues.actualAmount) : null,
      attendees: cleanAttendees,
      isUpdate: props.epliTrainerDevelopmentPlan.isUpdate,
      isImplementationPhase: props.implementationPhase
    }

    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(updatedPlan)

    if (!success) {
      errors?.forEach((error) => toast.error(error))
      return
    }

    toast.success('EPLI Trainer Plan Tracked')
    props.onSaved()
    props.onClose()
  }

  const renderTrackingFields = (): JSX.Element => {
    if (!isUpdate) {
      return <Typography variant='body1'>This plan has no updates to track.</Typography>
    }

    return (
      <Stack gap='1rem'>
        <Typography variant='h5'>Training</Typography>

        {/* Loop through attendees and show form for each attendee */}
        {formValues.attendees.map((attendee, index) => (
          <FormControl
            key={`${attendee.id ?? 0}-${index}`}
            variant='filled'
            required={attendeeFormValidators(index).completionStatus.required}
            error={submitted && !attendeeFormValidators(index).completionStatus.isValid}
          >
            <FormLabel id={`attendeeCompletionStatusLabel-${index}`}>How much of the training did {attendee.fullName} complete?</FormLabel>
            <RadioGroup
              aria-labelledby={`attendeeCompletionStatusLabel-${index}`}
              value={attendee.completionStatus ?? ''}
              id={`completionStatus-${index}`}
              name='completionStatus'
              data-testid={`completionStatus-${index}`}
              onChange={(e) => { handleAttendeeFieldUpdate(index, e) }}
            >
              {Object.values(EPLITrainerDevelopmentPlanAttendeeCompletionStatus).map(completionStatus => (
                <FormControlLabel
                  data-testid={`completionStatusOption-${index}-${completionStatus}`}
                  key={completionStatus}
                  value={completionStatus}
                  control={<Radio />}
                  label={completionStatus}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 14
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        ))}
        {formValues.attendees.length === 0 && (
          <Typography variant='body1'>No attendees have been added to this plan.</Typography>
        )}

        <Typography variant='h5'>Budget</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='body1'>
            How much funding was actually used?
            <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Optional</Typography>
          </Typography>
          <TextField
            id='budget-request'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Amount'}
            data-testid='actualBudgetAmount'
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            name='actualAmount'
            required={formValidators.actualAmount.required}
            error={submitted && !formValidators.actualAmount.isValid}
            value={formValues.actualAmount ?? ''}
          />
        </Box>
      </Stack>
    )
  }

  return (
    <Modal
      title='EPLI Trainer Tracking'
      open={props.open}
      data-testid='epliTrainerDevelopmentPlanTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>
        {renderTrackingFields()}
      </Stack>}
    />
  )
}
